'use client';

import React from 'react';
import { useTranslations } from '@/hooks/useTranslations';
import type { BlogCategoryRef, ArticleEntry } from '@/lib/cms';
import type { Locale } from '@/lib/i18n';
import { LocalizedLink } from '@/components/LocalizedLink';
import { BlogArticleCard } from './BlogArticleCard';
import { MotionReveal } from '@/components/MotionReveal';
import { MotionStagger } from '@/components/MotionStagger';

type BlogListingClientProps = {
  articles: ArticleEntry[];
  categories: BlogCategoryRef[];
  locale: Locale;
};

export function BlogListingClient({ articles, categories, locale }: BlogListingClientProps) {
  const { t } = useTranslations();
  const [search, setSearch] = React.useState('');
  const [categorySlug, setCategorySlug] = React.useState<string>('');
  const [tag, setTag] = React.useState<string>('');

  const allTags = React.useMemo(() => {
    const set = new Set<string>();
    articles.forEach((a) => (a.tags ?? []).forEach((x) => set.add(x)));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [articles]);

  const filtered = React.useMemo(() => {
    return articles.filter((a) => {
      if (categorySlug && a.category?.slug !== categorySlug) return false;
      if (tag && !(a.tags ?? []).includes(tag)) return false;
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        const blob = `${a.title} ${a.excerpt ?? ''} ${(a.tags ?? []).join(' ')}`.toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [articles, categorySlug, tag, search]);

  const featuredPool = filtered.filter((a) => a.featured);
  const featured =
    featuredPool.length > 0 ? featuredPool.slice(0, 3) : filtered.slice(0, 3);
  const latest = filtered;
  const popular = filtered.slice(0, 5);

  return (
    <div className="space-y-10">
      <MotionReveal as="section" className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 md:p-6">
        <div className="flex flex-col gap-4 md:flex-row md:flex-wrap md:items-end">
          <label className="flex min-w-[200px] flex-1 flex-col gap-1 text-sm text-slate-400">
            <span>{t('blog.filterSearch')}</span>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-slate-100 placeholder:text-slate-600 focus:border-[#ffb74d] focus:outline-none focus:ring-1 focus:ring-[#ffb74d]/40"
              placeholder={t('blog.searchPlaceholder')}
              type="search"
            />
          </label>
          <label className="flex min-w-[160px] flex-col gap-1 text-sm text-slate-400">
            <span>{t('blog.filterCategory')}</span>
            <select
              value={categorySlug}
              onChange={(e) => setCategorySlug(e.target.value)}
              className="rounded-lg border border-white/10 bg-[#0b0d19] px-3 py-2 text-slate-100 focus:border-[#ffb74d] focus:outline-none focus:ring-1 focus:ring-[#ffb74d]/40"
            >
              <option value="">{t('blog.allCategories')}</option>
              {categories.map((c) => (
                <option key={c.id} value={c.slug || ''}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex min-w-[160px] flex-col gap-1 text-sm text-slate-400">
            <span>{t('blog.filterTag')}</span>
            <select
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              className="rounded-lg border border-white/10 bg-[#0b0d19] px-3 py-2 text-slate-100 focus:border-[#ffb74d] focus:outline-none focus:ring-1 focus:ring-[#ffb74d]/40"
            >
              <option value="">{t('blog.allTags')}</option>
              {allTags.map((tg) => (
                <option key={tg} value={tg}>
                  {tg}
                </option>
              ))}
            </select>
          </label>
        </div>
      </MotionReveal>

      {featured.length > 0 ? (
        <MotionReveal as="section" className="space-y-4">
          <h2 className="text-lg font-semibold text-white">{t('blog.featured')}</h2>
          <MotionStagger className="grid gap-4 md:grid-cols-3" staggerStep={0.06}>
            {featured.map((article) => (
              <BlogArticleCard
                key={article.id}
                article={article}
                href={`/blog/${article.slug}`}
                locale={locale}
              />
            ))}
          </MotionStagger>
        </MotionReveal>
      ) : null}

      <MotionReveal as="section" className="space-y-4">
        <h2 className="text-lg font-semibold text-white">{t('blog.latest')}</h2>
        {latest.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/10 bg-white/5 p-8 text-center text-sm text-slate-400">
            {t('blog.noArticles')}
          </div>
        ) : (
          <MotionStagger className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" staggerStep={0.05}>
            {latest.map((article) => (
              <BlogArticleCard
                key={article.id}
                article={article}
                href={`/blog/${article.slug}`}
                locale={locale}
              />
            ))}
          </MotionStagger>
        )}
      </MotionReveal>

      {popular.length > 0 ? (
        <MotionReveal as="section" className="space-y-4">
          <h2 className="text-lg font-semibold text-white">{t('blog.popular')}</h2>
          <ol className="list-decimal space-y-2 rounded-2xl border border-white/10 bg-white/[0.03] py-4 pl-10 pr-4 text-sm text-slate-300">
            {popular.map((article) => (
              <li key={article.id} className="marker:text-[#ffb74d]">
                <LocalizedLink
                  href={`/blog/${article.slug}`}
                  className="text-slate-200 hover:text-[#ffb74d] hover:underline"
                >
                  {article.title}
                </LocalizedLink>
              </li>
            ))}
          </ol>
        </MotionReveal>
      ) : null}
    </div>
  );
}
